import { CaretDownIcon } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslations } from "use-intl";

import {
  rudderAnalytics,
  FlightTrackingEvents,
  HotelTrackingEvents,
  TripTrackingEvents,
} from "@/app/lib/rudderstack";
import { FilterButton } from "@/app/ui/FilterButton";
import { Drawer } from "@/components/Drawer/Drawer";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { CategorySwitcher } from "@/features/bookings/components/CategorySwitcher";
import { reservationTypesLabels } from "@/features/bookings/components/labels";
import { MyFlightsList } from "@/features/bookings/components/MyFlightsList";
import { MyHotelsList } from "@/features/bookings/components/MyHotelsList";
import { OrdersTypeList } from "@/features/bookings/components/OrdersTypeList";
import { MyTripsList } from "@/features/trips/components/bookingDetails/MyTripsList";
import { ReservationType } from "@/types/enums";
import type { SearchScope } from "@/types/types";

interface Props {
  category: SearchScope;
  initReservationType: ReservationType;
}

export const MyBookings = ({ category, initReservationType }: Props) => {
  const navigate = useNavigate();
  const t = useTranslations();
  const [selectedReservationType, setSelectedReservationType] =
    useState<ReservationType>(initReservationType);
  const [reservationTypeDrawerOpen, setReservationTypeDrawerOpen] =
    useState(false);

  const reservationTypeSwitcher = (
    <FilterButton onClick={() => setReservationTypeDrawerOpen(true)}>
      {t(reservationTypesLabels[selectedReservationType])}
      <CaretDownIcon aria-hidden className="shrink-0" weight="bold" />
    </FilterButton>
  );

  return (
    <>
      <div className="mb-4 flex justify-center">
        <CategorySwitcher current={category} />
      </div>

      <>
        {category === "flights" ? (
          <MyFlightsList
            onReservationClick={(reservationId) => {
              rudderAnalytics.track(
                FlightTrackingEvents.USER_ENTERED_PARTICULAR_ORDER,
              );
              navigate({
                to: "/bookings/$scope/$id",
                params: { scope: "flights", id: reservationId },
              });
            }}
            reservationType={selectedReservationType}
            reservationTypeSwitcher={reservationTypeSwitcher}
          />
        ) : category === "hotels" ? (
          <MyHotelsList
            onReservationClick={(reservationId) => {
              rudderAnalytics.track(
                HotelTrackingEvents.USER_ENTERED_PARTICULAR_ORDER,
              );
              navigate({
                to: "/bookings/$scope/$id",
                params: { scope: "hotels", id: reservationId },
              });
            }}
            reservationType={selectedReservationType}
            reservationTypeSwitcher={reservationTypeSwitcher}
          />
        ) : category === "trips" ? (
          <MyTripsList
            onReservationClick={(reservationId) => {
              rudderAnalytics.track(
                TripTrackingEvents.USER_ENTERED_PARTICULAR_ORDER,
              );
              navigate({
                to: "/bookings/$scope/$id",
                params: { scope: "trips", id: reservationId },
              });
            }}
          />
        ) : null}
      </>

      <Drawer
        open={reservationTypeDrawerOpen}
        onClose={() => setReservationTypeDrawerOpen(false)}
      >
        <DrawerTopBar
          onCloseClick={() => setReservationTypeDrawerOpen(false)}
          title={t("reservations.whichTripsToShow")}
        />

        <OrdersTypeList
          value={selectedReservationType}
          onValueChange={(newValue) => {
            setSelectedReservationType(newValue);
            setReservationTypeDrawerOpen(false);
          }}
        />
      </Drawer>
    </>
  );
};
